// Customer-facing message templates, one language per message, from chartegraphique/assets/multilingual-copy.md.
// Placeholders are variables; the order number and the pickup point are never truncated.
import { feedbackButtonId } from './inbound';
import type { Lang } from './ticket';

export interface MessageContext {
  orderNumber: string;
  venueName: string;
  pickupPoint?: string | null;
  token?: string;
  reviewUrl?: string | null;
  /** The number the order had before staff corrected it (`numberCorrected` only). */
  previousNumber?: string | null;
}

export type MessageKind =
  | 'prefill'
  | 'registered'
  | 'ready'
  | 'readySms'
  | 'numberCorrected'
  | 'feedbackQuestion'
  | 'feedbackFollowup'
  | 'help'
  | 'stopConfirm'
  | 'twoVenues'
  | 'callPermission';

/** SMS segment size: GSM-7 for Latin scripts, UCS-2 for Arabic. */
export const SMS_LIMIT: Record<Lang, number> = { fr: 160, en: 160, ar: 70 };

type Renderer = (c: MessageContext) => string;

// ---- the two messages a venue may rewrite (issue #20, US-5) ----

/** The messages an owner may rewrite per language; every other message keeps the platform copy. */
export const OVERRIDE_KINDS = ['registered', 'ready'] as const;
export type OverrideKind = (typeof OVERRIDE_KINDS)[number];

/** The variables a message text may carry, written `{{name}}`. */
export const MESSAGE_PLACEHOLDERS = ['order_number', 'venue_name', 'pickup_point'] as const;
export type MessagePlaceholder = (typeof MESSAGE_PLACEHOLDERS)[number];

/**
 * A venue's own texts, per language and message; a missing entry is the default. Stored texts were validated by
 * `checkMessageOverride` (messages.ts), so they keep their required placeholders and stay within the limit.
 */
export type MessageOverrides = Partial<Record<Lang, Partial<Record<OverrideKind, string>>>>;

/**
 * The default texts with their placeholders, as the settings page shows them before an owner edits anything and
 * after "restore the default". The ready text is the part above the closing line, which is always added below it.
 */
export const DEFAULT_MESSAGE_TEMPLATES: Record<Lang, Record<OverrideKind, string>> = {
  fr: {
    registered: 'Commande n° {{order_number}} chez {{venue_name}} : votre alerte est activée. Nous vous enverrons un message lorsque votre commande sera prête.',
    ready: 'Votre commande n° {{order_number}} chez {{venue_name}} est prête !\nRécupérez-la à {{pickup_point}}.',
  },
  en: {
    registered: "Order #{{order_number}} at {{venue_name}}: your alert is set. We'll send you a message when your order is ready.",
    ready: 'Your order #{{order_number}} at {{venue_name}} is ready!\nCollect it at {{pickup_point}}.',
  },
  ar: {
    registered: 'الطلب رقم {{order_number}} لدى {{venue_name}}: تم تفعيل التنبيه. سنرسل إليك رسالة عندما يصبح طلبك جاهزًا.',
    ready: 'طلبك رقم {{order_number}} لدى {{venue_name}} جاهز!\nيمكنك استلامه من {{pickup_point}}.',
  },
};

/**
 * The last line of every WhatsApp ready message, default or rewritten: it sets up the feedback question. A venue
 * cannot remove it; the SMS leaves it out.
 */
export const CLOSING_LINE: Record<Lang, string> = {
  fr: "Bon appétit, et n'oubliez pas de nous laisser votre avis.",
  en: "Enjoy your meal, and don't forget to leave us your feedback.",
  ar: 'بالهناء والشفاء، ولا تنسَ أن تترك لنا تقييمك.',
};

/** What `{{pickup_point}}` reads when a message has no pickup point name (the counter itself). */
const PICKUP_FALLBACK: Record<Lang, string> = { fr: 'comptoir', en: 'counter', ar: 'المنضدة' };

const PLACEHOLDER_RE = /\{\{(order_number|venue_name|pickup_point)\}\}/g;

/**
 * A message text with its variables replaced, in one pass: a venue or pickup point name that itself contains
 * braces is never read as a variable. Anything else between braces is left as typed (a draft being edited).
 */
export function fillMessage(template: string, c: MessageContext, lang: Lang): string {
  return template.replace(PLACEHOLDER_RE, (_, k: MessagePlaceholder) => (k === 'order_number' ? c.orderNumber : k === 'venue_name' ? c.venueName : (c.pickupPoint ?? PICKUP_FALLBACK[lang])));
}

/** The venue's text for this message and language, or null for the default. */
export function overrideFor(overrides: MessageOverrides | null | undefined, kind: OverrideKind, lang: Lang): string | null {
  const text = overrides?.[lang]?.[kind];
  return typeof text === 'string' && text.length > 0 ? text : null;
}

/** A ready message from its upper part: the closing line always comes last, on its own line. */
function withClosingLine(lang: Lang, body: string): string {
  return `${body}\n${CLOSING_LINE[lang]}`;
}

/**
 * Between the order number and the token in the prefilled WhatsApp message. A neutral character with a space on each
 * side keeps the two apart for the inbound router (`TOKEN_REGEX` wants non-alphanumerics around the token) and, in
 * Arabic, keeps them as two separate left-to-right runs.
 */
export const PREFILL_SEPARATOR = ' · ';

/**
 * The prefilled WhatsApp message: "<lead> <number> · <token>", the one thing the guest sends without typing.
 * The number and the token are Western digits and Latin capitals in every language. The Arabic sentence starts
 * with an Arabic letter, so the paragraph is right-to-left and the bidi algorithm renders the number and the token
 * each as its own left-to-right run, in reading order, with no control characters that a keyboard or a client
 * could show or strip.
 */
function prefill(lead: string, c: MessageContext): string {
  return c.token ? `${lead} ${c.orderNumber}${PREFILL_SEPARATOR}${c.token}` : `${lead} ${c.orderNumber}`;
}

const T: Record<Lang, Record<MessageKind, Renderer>> = {
  fr: {
    prefill: (c) => prefill('Prévenez-moi pour la commande', c),
    registered: (c) => fillMessage(DEFAULT_MESSAGE_TEMPLATES.fr.registered, c, 'fr'),
    ready: (c) =>
      withClosingLine('fr', c.pickupPoint ? fillMessage(DEFAULT_MESSAGE_TEMPLATES.fr.ready, c, 'fr') : `Votre commande n° ${c.orderNumber} chez ${c.venueName} est prête !\nRécupérez-la au comptoir.`),
    readySms: (c) => `${c.venueName} : votre commande n° ${c.orderNumber} est prête. Retrait : ${c.pickupPoint ?? 'comptoir'}.`,
    numberCorrected: (c) =>
      `Correction : votre commande chez ${c.venueName} porte désormais le n° ${c.orderNumber}` +
      (c.previousNumber ? ` (et non le n° ${c.previousNumber})` : '') +
      `. Nous vous enverrons un message lorsqu'elle sera prête.`,
    feedbackQuestion: (c) => `Comment était la commande n° ${c.orderNumber} chez ${c.venueName} ?`,
    feedbackFollowup: (c) =>
      c.reviewUrl
        ? `Merci ! Laissez un avis sur Google : ${c.reviewUrl}\nOu répondez ici pour une note privée au restaurant.`
        : `Merci ! Répondez ici pour une note privée au restaurant.`,
    help: () => `Scannez le code au comptoir pour activer votre alerte. Besoin d'aide ? Adressez-vous à l'équipe.`,
    stopConfirm: () => `Votre alerte est annulée. Pour suivre votre commande, adressez-vous à l'équipe.`,
    twoVenues: () => `Vous avez plusieurs commandes en cours. Laquelle ?`,
    callPermission: (c) => `Si votre commande n° ${c.orderNumber} vous attend au comptoir, ${c.venueName} peut vous appeler sur WhatsApp.`,
  },
  en: {
    prefill: (c) => prefill('Notify me for order', c),
    registered: (c) => fillMessage(DEFAULT_MESSAGE_TEMPLATES.en.registered, c, 'en'),
    ready: (c) =>
      withClosingLine('en', c.pickupPoint ? fillMessage(DEFAULT_MESSAGE_TEMPLATES.en.ready, c, 'en') : `Your order #${c.orderNumber} at ${c.venueName} is ready!\nCollect it at the counter.`),
    readySms: (c) => `${c.venueName}: your order #${c.orderNumber} is ready. Pickup: ${c.pickupPoint ?? 'counter'}.`,
    numberCorrected: (c) =>
      `Correction: your order at ${c.venueName} is now #${c.orderNumber}` + (c.previousNumber ? ` (not #${c.previousNumber})` : '') + `. We'll send you a message when it's ready.`,
    feedbackQuestion: (c) => `How was order #${c.orderNumber} at ${c.venueName}?`,
    feedbackFollowup: (c) =>
      c.reviewUrl
        ? `Thanks! Leave a Google review: ${c.reviewUrl}\nOr reply here with a private note to the restaurant.`
        : `Thanks! Reply here with a private note to the restaurant.`,
    help: () => `Scan the code at the counter to set your alert. Need help? Ask our team.`,
    stopConfirm: () => `Your alert is cancelled. Ask our team for an update on your order.`,
    twoVenues: () => `You have more than one order in progress. Which one?`,
    callPermission: (c) => `If order #${c.orderNumber} is waiting for you at the counter, ${c.venueName} can call you on WhatsApp.`,
  },
  ar: {
    prefill: (c) => prefill('أبلغوني عندما يجهز الطلب', c),
    registered: (c) => fillMessage(DEFAULT_MESSAGE_TEMPLATES.ar.registered, c, 'ar'),
    ready: (c) =>
      withClosingLine('ar', c.pickupPoint ? fillMessage(DEFAULT_MESSAGE_TEMPLATES.ar.ready, c, 'ar') : `طلبك رقم ${c.orderNumber} لدى ${c.venueName} جاهز!\nيمكنك استلامه من المنضدة.`),
    readySms: (c) => `${c.venueName}: طلبك رقم ${c.orderNumber} جاهز. الاستلام من ${c.pickupPoint ?? 'المنضدة'}.`,
    numberCorrected: (c) =>
      `تصحيح: أصبح رقم طلبك لدى ${c.venueName} هو ${c.orderNumber}` + (c.previousNumber ? ` (وليس ${c.previousNumber})` : '') + `. سنرسل إليك رسالة عندما يصبح جاهزًا.`,
    feedbackQuestion: (c) => `كيف كان الطلب رقم ${c.orderNumber} لدى ${c.venueName}؟`,
    feedbackFollowup: (c) =>
      c.reviewUrl ? `شكرًا لك! اترك تقييمًا على Google: ${c.reviewUrl}\nأو رد هنا بملاحظة خاصة للمطعم.` : `شكرًا لك! رد هنا بملاحظة خاصة للمطعم.`,
    help: () => `امسح الرمز عند المنضدة لتفعيل التنبيه. هل تحتاج إلى مساعدة؟ تواصل مع فريقنا.`,
    stopConfirm: () => `تم إلغاء التنبيه. لمعرفة حالة طلبك، تواصل مع فريقنا.`,
    twoVenues: () => `لديك أكثر من طلب قيد التحضير. أيّها تقصد؟`,
    callPermission: (c) => `إذا كان طلبك رقم ${c.orderNumber} بانتظارك عند المنضدة، يمكن لـ ${c.venueName} الاتصال بك عبر واتساب.`,
  },
};

/**
 * One message in one language. `overrides` are the venue's own texts (issue #20): they replace the registered and
 * ready messages of their language, the ready one still ending with the closing line; every other kind, and a
 * language the venue did not rewrite, keeps the default.
 */
export function renderMessage(kind: MessageKind, lang: Lang, ctx: MessageContext, overrides?: MessageOverrides | null): string {
  if (kind === 'registered' || kind === 'ready') {
    const own = overrideFor(overrides, kind, lang);
    if (own) return kind === 'ready' ? withClosingLine(lang, fillMessage(own, ctx, lang)) : fillMessage(own, ctx, lang);
  }
  return T[lang][kind](ctx);
}

/** Renders with a shorter and shorter venue name until the text fits `limit`; the number and the pickup point are never cut. */
function shrinkVenue(ctx: MessageContext, limit: number, render: (c: MessageContext) => string): string {
  let venue = ctx.venueName;
  let text = render({ ...ctx, venueName: venue });
  while (text.length > limit && venue.length > 4) {
    venue = venue.slice(0, -1).trimEnd();
    text = render({ ...ctx, venueName: venue });
  }
  return text;
}

/**
 * The ready SMS, without the closing line. A venue's own ready text (issue #20) is used when it fits one segment,
 * the venue name shortened if needed; otherwise, and for every venue without one, the concise default is used,
 * shortened to one segment by trimming only the venue name. `source` says which one goes out.
 */
export function renderSms(lang: Lang, ctx: MessageContext, overrides?: MessageOverrides | null): { text: string; segments: number; source: 'override' | 'default' } {
  const limit = SMS_LIMIT[lang];
  const own = overrideFor(overrides, 'ready', lang);
  if (own) {
    const text = shrinkVenue(ctx, limit, (c) => fillMessage(own, c, lang));
    if (text.length <= limit) return { text, segments: 1, source: 'override' };
  }
  const text = shrinkVenue(ctx, limit, T[lang].readySms);
  return { text, segments: Math.max(1, Math.ceil(text.length / limit)), source: 'default' };
}

export const FEEDBACK_BUTTONS: Record<Lang, { id: string; title: string }[]> = {
  fr: [
    { id: feedbackButtonId(3), title: 'Excellent' },
    { id: feedbackButtonId(2), title: 'Bien' },
    { id: feedbackButtonId(1), title: 'Pas terrible' },
  ],
  en: [
    { id: feedbackButtonId(3), title: 'Excellent' },
    { id: feedbackButtonId(2), title: 'Good' },
    { id: feedbackButtonId(1), title: 'Not great' },
  ],
  ar: [
    { id: feedbackButtonId(3), title: 'ممتاز' },
    { id: feedbackButtonId(2), title: 'جيد' },
    { id: feedbackButtonId(1), title: 'غير جيد' },
  ],
};

/**
 * Meta message templates (docs/whatsapp-templates.md): used only outside a customer-initiated window. Names, language
 * codes and parameter order are a contract with WhatsApp Manager; do not rename here without renaming there.
 */
export const WA_TEMPLATES = {
  /**
   * Authentication template, copy-code button, 10-minute expiry. Parameter {{1}} is the 6-digit sign-in code.
   * Only the English version (`en`) is approved in WhatsApp Manager (16 Oct 2026), so every page language sends it;
   * map `fr` and `ar` back to their own codes once those versions are approved (docs/whatsapp-templates.md).
   */
  loginCode: { name: 'ringring_login_code', language: { fr: 'en', ar: 'en', en: 'en' } as Record<Lang, string> },
} as const;

export function googleReviewUrl(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

/** The wa.me link without its text, for a page that rebuilds the prefill when the order number changes. */
export function whatsappLinkBase(platformNumber: string): string {
  return `https://wa.me/${platformNumber.replace(/\D/g, '')}?text=`;
}

export function whatsappLink(platformNumber: string, text: string): string {
  return `${whatsappLinkBase(platformNumber)}${encodeURIComponent(text)}`;
}
